import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Shield, Search, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/scam-check', label: 'Scam Check' },
    { path: '/investigations', label: 'Investigations' },
    { path: '/scam-database', label: 'Fraud Database' },
    { path: '/report-a-scam', label: 'Report a Scam' },
    { path: '/scam-library', label: 'Guides' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#0a1628]/98 backdrop-blur-md shadow-lg shadow-blue-900/20 border-b border-[#1e40af]/20' 
          : 'bg-[#0a1628]/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo with Premium Animated Shine Effect */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="logo-shine-container relative">
              {/* Subtle blue glow behind logo */}
              <div className="absolute inset-0 bg-blue-500/15 blur-2xl rounded-full scale-125 opacity-70" />
              <img
                src="/actionfraudone-logo-transparent.png"
                alt="ActionFraudOne"
                className="h-12 w-auto relative z-10 logo-premium-glow logo-transparent"
                loading="eager"
                decoding="async"
                draggable={false}
              />
            </div>
            <div className="hidden lg:block">
              <span className="text-blue-400 font-semibold uppercase text-xs tracking-wider block">
                ActionFraudOne<sup className="text-[0.7em] text-blue-300/70 ml-0.5 font-normal">™</sup>
              </span>
              <p className="text-gray-500 text-[10px] tracking-wide">Verify. Protect. Take Action.</p>
            </div>
          </Link>

          {/* Desktop Navigation - Compact & Corporate */}
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-[13px] font-medium transition-all relative whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-cyan-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full" />
                  )}
                </Link>
              ))}
            </div>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* WhatsApp Icon */}
            <a
              href="https://wa.me/447386668966"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex w-10 h-10 rounded-full bg-green-500/10 border border-green-500/30 items-center justify-center text-green-400 hover:bg-green-500/20 transition-all"
              aria-label="Contact via WhatsApp"
            >
              <WhatsAppIcon size={18} />
            </a>

            {/* CTA Button */}
            <Link to="/scam-check" className="hidden sm:block">
              <Button className="bg-gradient-to-r from-[#1e40af] to-cyan-600 hover:from-[#1e3a8a] hover:to-cyan-500 text-white font-semibold px-4 py-2 text-sm rounded-lg shadow-lg shadow-blue-500/20 transition-all">
                <Search className="w-4 h-4 mr-2" />
                Check a Scam
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#1e40af]/20">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                    isActive(link.path)
                      ? 'text-cyan-400 bg-[#1e40af]/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 px-4 flex gap-3">
                <a
                  href="https://wa.me/447386668966"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400"
                >
                  <WhatsAppIcon size={18} />
                  WhatsApp
                </a>
                <Link to="/scam-check" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-600 text-white font-semibold py-3">
                    <Search className="w-4 h-4 mr-2" />
                    Check Scam
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Logo Scan Animation Styles */}
      <style>{`
        .logo-scan-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 4px;
        }
        
        .logo-scan-line {
          position: absolute;
          top: 0;
          left: -100%;
          width: 40%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(34, 211, 238, 0) 20%,
            rgba(34, 211, 238, 0.15) 40%,
            rgba(103, 232, 249, 0.3) 50%,
            rgba(34, 211, 238, 0.15) 60%,
            rgba(34, 211, 238, 0) 80%,
            transparent 100%
          );
          animation: logoScanSweep 3s ease-in-out infinite;
          animation-delay: 1s;
          pointer-events: none;
          z-index: 20;
        }
        
        @keyframes logoScanSweep {
          0% {
            left: -40%;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 0;
          }
        }
        
        .logo-scan-container:hover .logo-scan-line {
          animation-duration: 1.5s;
        }
      `}</style>
    </header>
  );
};

export default Header;
